import { useGetProductsList, useGetStockItemsList } from '@agos/api-client/menu'
import type { LinkableProduct, StockItemOption } from '@agos/api-client/model'
import { T, formatMoney, parseMoneyToCents, sumCents, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import { Package, Plus, X } from 'lucide-react'

import { derivedCost } from './ingredient-cost'
import { StockItemPicker } from './StockItemPicker'

/**
 * The recipe block, drawn in the two places a recipe is edited — FRONTEND.md §9.221.
 *
 * **Extracted rather than copied.** Legacy edits a recipe inside the product form and AGOS had it
 * only in a modal of its own; putting it in both meant either two copies of an ingredient row or one
 * component. Two copies is how the two drift, and this screen has been reported four times.
 *
 * The rows themselves are unchanged — they were already legacy's, down to the `P`/`S` badge and the
 * violet tint on a product against the primary tint on a stock item.
 */

export const INGREDIENT_UNITS = ['g', 'kg', 'ml', 'l', 'ks', 'dl']

const SMALL_FIELD_CLASSES =
  'px-3 py-2 rounded-xl bg-background border border-border text-xs outline-none focus:ring-2 focus:ring-primary/20'

export interface IngredientDraft {
  stockItemId: number | null
  productId: number | null
  name: string
  quantity: string
  unit: string
  cost: string
  /**
   * Whether somebody typed over the derived figure — §9.227.
   *
   * The owner asked for *"no manual pricing… (or manually adjusted)"*, and the parenthesis matters:
   * on this data no stock item carries a price, so a row that could not derive one still needs a
   * number. Once typed, the derivation stops overwriting it.
   */
  costTouched?: boolean
}

export const EMPTY_INGREDIENT: IngredientDraft = {
  stockItemId: null,
  productId: null,
  name: '',
  quantity: '',
  unit: 'g',
  cost: '0',
}

/**
 * What the rows cost, and — where there is a price to compare it against — what is left of it.
 *
 * **`sellingPriceCents` is null in the standalone modal and a figure in the product form**, which is
 * the whole reason §9.221 moved this: the margin can only be shown beside the price it is a margin
 * on, and in the product form that price is the field two inches above.
 */
export function recipeTotals(
  ingredients: readonly IngredientDraft[],
  sellingPriceCents: number | null,
): { costCents: number; marginCents: number | null; marginPercent: number | null } {
  const costCents = sumCents(...ingredients.map((row) => row.cost))
  if (sellingPriceCents === null) return { costCents, marginCents: null, marginPercent: null }
  const marginCents = sellingPriceCents - costCents
  return {
    costCents,
    marginCents,
    // A margin against a price of nought is not 100 % and not zero — it is undefined, and printing
    // either would put a number on a screen that means nothing.
    marginPercent: sellingPriceCents > 0 ? (marginCents / sellingPriceCents) * 100 : null,
  }
}

export function RecipeEditor({
  ingredients,
  onChange,
  sellingPriceCents = null,
}: {
  ingredients: IngredientDraft[]
  onChange: (next: IngredientDraft[]) => void
  /** The product's own price, when this is drawn inside the product form. */
  sellingPriceCents?: number | null
}) {
  const { data: products } = useGetProductsList()
  const { data: stockItems } = useGetStockItemsList()

  const itemFor = (row: IngredientDraft) =>
    row.stockItemId === null
      ? null
      : ((stockItems ?? []).find((option) => option.id === row.stockItemId) ?? null)

  /**
   * Apply the change, then re-derive the cost from it — §9.227.
   *
   * Done here rather than in an effect: the cost depends on the quantity, the unit and the chosen
   * item, all of which change through this one function, and a render-phase `setState` is what
   * `ci.sh --strict` refuses. A row somebody has typed a cost into keeps it.
   */
  const patch = (index: number, changes: Partial<IngredientDraft>) =>
    onChange(
      ingredients.map((row, position) => {
        if (position !== index) return row
        const next = { ...row, ...changes }
        if (next.costTouched === true) return next
        const derived = derivedCost(next, itemFor(next))
        // `String(round)` rather than `toFixed`: this is the value of an `<input type="number">`,
        // which must be dot-decimal to be valid, so N-02's comma formatters do not apply here.
        return derived.euros === null
          ? next
          : { ...next, cost: String(Math.round(derived.euros * 100) / 100) }
      }),
    )

  const totals = recipeTotals(ingredients, sellingPriceCents)

  return (
    <div className="space-y-2" data-recipe-editor>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Package className="w-3 h-3" /> {ingredients.length} <T>ingrediencií</T>
        </span>
        <button
          type="button"
          data-add-ingredient
          onClick={() => onChange([...ingredients, { ...EMPTY_INGREDIENT }])}
          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-muted text-xs font-semibold hover:bg-muted-foreground/20 transition-colors"
        >
          <Plus className="w-3 h-3" /> <T>Pridať</T>
        </button>
      </div>

      <div className="space-y-2">
        {ingredients.map((row, index) => (
          <div
            key={index}
            data-ingredient-row={index}
            className={cn(
              'flex gap-2 items-center rounded-lg px-1.5 py-0.5 -mx-1.5',
              row.productId ? 'bg-violet-500/5' : row.stockItemId ? 'bg-primary/5' : '',
            )}
          >
            {(row.stockItemId !== null || row.productId !== null) && (
              <span
                className={cn(
                  'text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0',
                  row.productId ? 'bg-violet-500/15 text-violet-500' : 'bg-primary/15 text-primary',
                )}
              >
                {row.productId ? 'P' : 'S'}
              </span>
            )}

            <StockItemPicker
              stockItems={stockItems ?? []}
              products={products ?? []}
              stockItemId={row.stockItemId}
              productId={row.productId}
              displayName={row.name}
              onSelect={(stockItem: StockItemOption | null, product: LinkableProduct | null) =>
                patch(index, {
                  stockItemId: stockItem?.id ?? null,
                  productId: product?.id ?? null,
                  name: stockItem?.name ?? product?.name ?? row.name,
                  unit: stockItem?.unit ?? (product ? 'ks' : row.unit),
                })
              }
            />

            <input
              type="number"
              step="0.01"
              min="0.01"
              value={row.quantity}
              onChange={(event) => patch(index, { quantity: event.target.value })}
              placeholder={tt('Množstvo')}
              aria-label={tt('Množstvo')}
              className={cn(SMALL_FIELD_CLASSES, 'w-24')}
            />
            <select
              value={row.unit}
              onChange={(event) => patch(index, { unit: event.target.value })}
              aria-label={tt('Jednotka')}
              className={cn(SMALL_FIELD_CLASSES, 'w-20 px-2')}
            >
              {INGREDIENT_UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
            {/* §9.227 - derived from the item's price and the quantity, and typed over only when
              * there is nothing to derive from. `data-cost-derived` says which of the two it is. */}
            <input
              type="number"
              step="0.01"
              min="0"
              value={row.cost}
              data-cost-derived={derivedCost(row, itemFor(row)).reason}
              onChange={(event) => patch(index, { cost: event.target.value, costTouched: true })}
              placeholder={tt('Náklad €')}
              aria-label={tt('Náklad €')}
              title={
                derivedCost(row, itemFor(row)).reason === 'no-price'
                  ? tt('Táto surovina nemá nákupnú cenu - doplňte ju v sklade alebo zadajte ručne')
                  : tt('Náklad €')
              }
              className={cn(
                SMALL_FIELD_CLASSES,
                'w-24',
                derivedCost(row, itemFor(row)).reason === 'ok' && row.costTouched !== true
                  ? 'text-muted-foreground'
                  : '',
              )}
            />
            <button
              type="button"
              aria-label={tt('Odstrániť ingredienciu')}
              onClick={() => onChange(ingredients.filter((_, position) => position !== index))}
              className="text-muted-foreground hover:text-red-500 transition-colors shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* §9.221 - legacy's summary. Cost alone where there is no price to compare it with; cost,
        * price and margin where there is, which is inside the product form. */}
      <div className="rounded-xl bg-muted/50 border border-border p-3 space-y-1.5" data-recipe-totals>
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">
            <T>Celkový náklad:</T>
          </span>
          <span className="font-bold" data-recipe-cost>
            {formatMoney(totals.costCents / 100)}
          </span>
        </div>
        {sellingPriceCents !== null && (
          <>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">
                <T>Predajná cena:</T>
              </span>
              <span className="font-bold">{formatMoney(sellingPriceCents / 100)}</span>
            </div>
            <div className="flex justify-between text-xs border-t border-border pt-1.5">
              <span className="text-muted-foreground">
                <T>Marža:</T>
              </span>
              <span
                className={cn(
                  'font-bold',
                  (totals.marginCents ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600',
                )}
                data-recipe-margin={totals.marginCents ?? 0}
              >
                {formatMoney((totals.marginCents ?? 0) / 100)}
                {totals.marginPercent === null ? '' : ` (${Math.round(totals.marginPercent)} %)`}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/** The rows a save should send: the ones somebody actually started filling in. */
export function startedRows(ingredients: readonly IngredientDraft[]): IngredientDraft[] {
  return ingredients.filter(
    (row) => row.name.trim() !== '' || row.quantity.trim() !== '' || row.stockItemId !== null || row.productId !== null,
  )
}

/**
 * Which row is unusable, or −1.
 *
 * FB-018: legacy filters incomplete rows out and saves anyway, so a forgotten quantity silently
 * drops an ingredient — and with it part of the dish's cost. Refuse, and say which row.
 */
export function firstIncompleteRow(ingredients: readonly IngredientDraft[]): number {
  return startedRows(ingredients).findIndex(
    (row) => row.name.trim() === '' || !(Number(row.quantity) > 0),
  )
}

/**
 * Whether a save would clear every ingredient off a recipe that has some - §9.363, FB-650.
 *
 * **`PUT /recipes/{id}` refuses that with a 409**, and the sentence it refuses with is written for a
 * developer: *"Recept má ingrediencie - pre vyčistenie pošli `?allowEmpty=1` alebo zmaž recept."*
 * Emptying the form and pressing save put that on the operator's screen, because `onError` shows the
 * server's own words - which is §6's rule working as intended on a message that was never meant for
 * them.
 *
 * **Why this refuses instead of sending the flag.** Legacy's comment on that guard says what it is
 * for: *"Guardrail against the 'ingrediencie sa niekedy odpárujú' bug - if the client somehow sends
 * an empty ingredients array for an existing recipe that DOES have ingredients, refuse rather than
 * silently wipe them."* It is a net under a client bug, strung after the owner lost ingredients. A
 * screen that passes `allowEmpty=1` whenever the form is empty is a client that has cut the net down
 * for exactly the case it was there for.
 *
 * **The loaded count, not the recipe's id.** A recipe stored with no ingredients may be saved with
 * none - the server's count check finds nothing to protect and allows it - so keying off *this is an
 * edit* would refuse a legitimate no-op save on an empty recipe.
 */
export function wouldClearIngredients(
  loadedCount: number,
  ingredients: readonly IngredientDraft[],
): boolean {
  return loadedCount > 0 && startedRows(ingredients).length === 0
}

/**
 * What to say instead of the server's sentence.
 *
 * Names both things the operator might have meant, because the server's own message does: keep the
 * ingredients, or delete the recipe. Saying only *this is not allowed* would leave somebody who
 * genuinely wants the recipe gone with nowhere to go.
 */
export function clearIngredientsRefusal(): string {
  return tt(
    'Recept musí mať aspoň jednu ingredienciu. Ak už ho nepotrebujete, zmažte celý recept.',
  )
}

/** What `POST /recipes` and `PUT /recipes/{id}` take for the ingredient list. */
export function ingredientPayload(ingredients: readonly IngredientDraft[]) {
  return startedRows(ingredients).map((row) => ({
    stockItemId: row.stockItemId ?? undefined,
    productId: row.productId ?? undefined,
    name: row.name.trim(),
    quantity: Number(row.quantity),
    unit: row.unit.trim() || 'g',
    cost: parseMoneyToCents(row.cost) / 100,
  }))
}
